import React, { useState, useRef, useEffect } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useTheme } from '../../hooks/useTheme'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'
import { AuthServiceUAU } from '../../services/authServiceUAU'
import { ChangePasswordModal } from '../../components/auth/ChangePasswordModal'

const AdminLayout: React.FC = () => {
  const { user: usuario, cerrarSesion, isLoading, tienePermiso } = useAuth()
  const { colors } = useTheme()
  const { deviceType } = useDeviceDetection()
  const navigate = useNavigate()
  const empresaActiva = usuario?.empresa_activa
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isMobileDevice = deviceType === 'mobile'

  // Cerrar dropdown cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  const handleLogout = async () => {
    try {
      await cerrarSesion()
      navigate('/')
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }

  const closeSidebar = () => {
    setSidebarOpen(false)
  }

  // Si está cargando, mostrar loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <div
            className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full mx-auto mb-4"
            style={{ borderColor: `${colors.primary}40`, borderTopColor: 'transparent' }}
          ></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    )
  }

  // Si no está autenticado, mostrar mensaje
  if (!usuario) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Área Protegida</h2>
          <p className="text-gray-600 mb-6">
            Esta área requiere autenticación. Serás redirigido al sistema de login.
          </p>
          <div
            className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full mx-auto"
            style={{ borderColor: `${colors.primary}40`, borderTopColor: 'transparent' }}
          ></div>
        </div>
      </div>
    )
  }

  // Verificar permisos específicos usando el contexto
  const tienePermisoAdmin = tienePermiso('admin.administrar')
  const tienePermisoPagos = tienePermiso('pagos.ver')
  const tienePermisoQRValidar = tienePermiso('qr.validar')
  const tienePermisoQRVer = tienePermiso('qr.ver')
  const tienePermisoBloqueos = tienePermiso('bloqueos.ver')
  const tienePermisoReservas = tienePermiso('reservas.ver')
  const tienePermisoReportes = tienePermiso('reportes.ver')
  const tienePermisoControl = tienePermiso('control.ver')
  const tienePermisoCamping = tienePermiso('camping.ver')

  const navigationItems = [
    // Dashboard - Dynamic route based on permission
    ...(tienePermisoAdmin
      ? [{ path: '/admin/dashboard', label: 'Dashboard', icon: '📊' }]
      : [{ path: '/admin/default-dashboard', label: 'Dashboard', icon: '📊' }]
    ),

    // Reservas - Requiere permiso específico
    ...(tienePermisoReservas ? [{ path: '/admin/reservas', label: 'Reservas', icon: '📅' }] : []),

    // QR Scanner - Requiere permiso
    ...(tienePermisoQRValidar ? [{ path: '/admin/qr-scanner', label: 'Validador QR', icon: '📱' }] : []),

    // QR Localizador - Requiere permiso
    ...(tienePermisoQRVer ? [{ path: '/admin/qr-localizador', label: 'Localizador de Espacios', icon: '📍' }] : []),

    // Pagos - Requiere permiso
    ...(tienePermisoPagos ? [{ path: '/admin/pagos', label: 'Pagos', icon: '💳' }] : []),

    // Espacios - Solo admin
    ...(tienePermisoAdmin ? [{ path: '/admin/espacios', label: 'Espacios', icon: '🏕️' }] : []),

    // Tipos de Espacios - Solo admin
    ...(tienePermisoAdmin ? [{ path: '/admin/tipos-espacios', label: 'Tipos de Espacios', icon: '🏠' }] : []),

    // Calendario - Solo admin
    ...(tienePermisoAdmin ? [{ path: '/admin/calendario', label: 'Calendario', icon: '🗓️' }] : []),

    // Vista Panorámica - Requiere permiso camping.ver
    ...(tienePermisoCamping ? [{ path: '/admin/ocupacion-panoramica', label: 'Vista Panorámica', icon: '🗺️' }] : []),

    // Bloqueos - Requiere permiso
    ...(tienePermisoBloqueos ? [{ path: '/admin/bloqueos', label: 'Bloqueos', icon: '🔒' }] : []),

    // Reportes - Requiere permiso
    ...(tienePermisoReportes ? [{ path: '/admin/reportes', label: 'Reportes', icon: '📈' }] : []),

    // Control - Requiere permiso
    ...(tienePermisoControl ? [{ path: '/admin/control', label: 'Control', icon: '🔍' }] : []),

    // Configuración - Solo admin
    ...(tienePermisoAdmin ? [{ path: '/admin/configuracion', label: 'Configuración', icon: '⚙️' }] : []),
  ]

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Overlay para cerrar sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - Oculta por defecto, se muestra con hamburguesa */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-64 shadow-lg transition-transform duration-300 ease-out ${
          sidebarOpen
            ? 'translate-x-0'
            : '-translate-x-full'
        }`}
        style={{
          backgroundColor: colors.primaryDark
        }}
      >
        {/* Logo */}
        <div className="flex items-center justify-center h-16 px-4" style={{
          backgroundColor: colors.primary
        }}>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`
            }}>
              <span className="text-white font-bold">🏕️</span>
            </div>
            <div className="text-white">
              <div className="font-bold text-sm">Admin Panel</div>
              <div className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Camping System</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-4 px-3">
          {navigationItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `flex items-center space-x-2 px-3 py-2 rounded text-sm transition-colors text-white`
              }
              style={({ isActive }) => ({
                backgroundColor: isActive ? colors.primary : 'transparent',
                opacity: isActive ? 1 : 0.8
              })}
              onMouseEnter={(e) => {
                if (!e.currentTarget.classList.contains('active')) {
                  e.currentTarget.style.backgroundColor = `${colors.primary}40`
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.classList.contains('active')) {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }
              }}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Profile with Company Selector */}
        <div className="absolute bottom-0 left-0 right-0 p-3" style={{
          backgroundColor: colors.primary
        }}>
          <div>
            {/* Selector de empresa usando las empresas asignadas del usuario */}
            {usuario?.empresas_asignadas && usuario.empresas_asignadas.length > 1 && (
              <div className="mb-2">
                <p className="text-xs mb-1" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Empresa:</p>
                <select
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2"
                  style={{ focusRing: colors.primary }}
                  value={empresaActiva?.id || (usuario.empresas_asignadas && usuario.empresas_asignadas[0]?.id) || ''}
                  onChange={(e) => {
                    const empresaId = parseInt(e.target.value)
                    const selectedEmpresa = usuario.empresas_asignadas.find(emp => emp.id === empresaId)
                    if (selectedEmpresa) {
                      AuthServiceUAU.setEmpresaActiva(selectedEmpresa)
                      // Recargar la página para actualizar los datos
                      window.location.reload()
                    }
                  }}
                >
                  {usuario.empresas_asignadas.map((empresa) => (
                    <option key={empresa.id} value={empresa.id}>
                      {empresa.nombre}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* User Info */}
            <div className="p-2 rounded text-xs" style={{
              backgroundColor: `${colors.primaryDark}40`
            }}>
              <p className="text-white font-medium truncate">{usuario?.nombre_completo || usuario?.username}</p>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)' }} className="truncate">{usuario?.email}</p>
              <button
                onClick={handleLogout}
                className="mt-1 w-full text-white text-xs py-1 px-2 rounded transition-colors"
                style={{
                  backgroundColor: colors.error
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Hamburger Menu */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="mr-4 p-2 rounded-lg transition-colors"
              style={{
                backgroundColor: sidebarOpen ? colors.primary : '#f3f4f6',
                color: sidebarOpen ? 'white' : colors.primary
              }}
              title="Menú"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={sidebarOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Administración</h1>
              <p className="text-gray-600 text-sm">
                {empresaActiva?.nombre || 'Sistema de Camping'}
              </p>
            </div>
            <div className={`flex items-center ${isMobileDevice ? 'space-x-2' : 'space-x-4'}`}>
              {!isMobileDevice && (
                <>
                  <div
                    className="flex items-center space-x-2 px-3 py-1 rounded-full text-sm"
                    style={{
                      backgroundColor: `${colors.success}15`,
                      color: colors.success
                    }}
                  >
                    <span className="w-2 h-2 animate-pulse" style={{ backgroundColor: colors.success }}></span>
                    <span>Sistema Activo</span>
                  </div>
                  <button
                    onClick={() => navigate('/')}
                    className="px-4 py-2 text-sm rounded-md font-semibold transition-all duration-300 ease-out cursor-pointer"
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: colors.primary,
                      border: `1px solid #d1d5db`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e5e7eb'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6'
                    }}
                  >
                    🌐 Ver Sitio Público
                  </button>
                  {/* User Dropdown Menu with Avatar */}
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center space-x-2 rounded-lg transition-colors hover:opacity-80"
                      style={{
                        backgroundColor: isDropdownOpen ? `${colors.primary}20` : 'transparent'
                      }}
                      title={usuario?.nombre_completo || usuario?.username}
                    >
                      {/* Avatar Badge */}
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md" style={{
                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`
                      }}>
                        {(usuario?.nombre_completo || usuario?.username || 'U')
                          .split(' ')
                          .map(word => word[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)}
                      </div>
                      {/* Down Arrow */}
                      <svg
                        className={`w-4 h-4 transition-transform mr-2 ${isDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        style={{ color: colors.primary }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
                        {/* Header */}
                        <div className="px-4 py-3" style={{ backgroundColor: `${colors.primary}10`, borderBottom: `1px solid ${colors.primary}20` }}>
                          <p className="text-sm font-semibold" style={{ color: colors.primary }}>
                            {usuario?.nombre_completo || usuario?.username}
                          </p>
                          <p className="text-xs text-gray-600">
                            {usuario?.email}
                          </p>
                        </div>

                        {/* Menu Items */}
                        <div className="py-2">
                          {/* Change Password */}
                          <button
                            onClick={() => {
                              setIsPasswordModalOpen(true)
                              setIsDropdownOpen(false)
                            }}
                            className="w-full px-4 py-2 text-left text-sm flex items-center gap-3 transition-colors"
                            style={{
                              color: colors.primary,
                              backgroundColor: 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = `${colors.primary}10`
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent'
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <span>Cambiar Contraseña</span>
                          </button>

                          {/* Separator */}
                          <div style={{ borderTop: `1px solid #e5e7eb`, margin: '0.5rem 0' }} />

                          {/* Logout */}
                          <button
                            onClick={handleLogout}
                            className="w-full px-4 py-2 text-left text-sm flex items-center gap-3 transition-colors text-red-600"
                            style={{
                              backgroundColor: 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#fee2e2'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent'
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            <span>Cerrar Sesión</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              {isMobileDevice && (
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg font-semibold transition-all duration-300 ease-out cursor-pointer"
                  style={{
                    backgroundColor: 'transparent',
                    color: colors.primary
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${colors.primary}10`
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                  title="Cerrar Sesión"
                >
                  🚪
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSuccess={() => {
          setIsPasswordModalOpen(false)
        }}
      />
    </div>
  )
}

export default AdminLayout